import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FindOneSubscriptionResponseDto,
  OrganizationsService,
  PrismaSubscriptionProductDto,
  SubscriptionsService,
} from 'app/API';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAsOnePlansQueryFn } from 'app/queries/billing/createFetchAsOnePlansQueryFn';
import { subscriptionsKeys } from 'app/queries/subscriptions/subscriptionsKeys';
import { createContext, ReactNode, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { createFetchSubscriptionQueryFn } from '../api/fetchSubscription';
import { getGlobalBackupRecordingValue } from '../helpers/getGlobalBackupRecordingValue';

type TState = {
  isLoading: boolean;
  isLoadError: boolean;
  retryLoad: () => void;
  search: string;
  setSearch: (v: string) => void;
  removeAssignee: (v: string) => void;
  editTranscriptUnlockType: (v: string, v2: string) => void;
  editAllTranscriptUnlockTypes: () => void;
  editBackupRecordingFlag: (v: string, v2: boolean) => void;
  setGlobalAS1Backup: (v: string) => void;
  unlockAITranscripts: () => void;
  onAddUserClick: (v: string | null) => void;
  assignedCount: number;
  unassignedCount: number;
  onAssignLicense: (v: Assignee, v2: string | null) => void;
  subscription: FindOneSubscriptionResponseDto | null;
  plan: PrismaSubscriptionProductDto | undefined;
  globalLicensesBackupEnabledValue: string;
  openAS1BackupTerms: () => void;
};

const PageContext = createContext<TState>({
  isLoading: true,
  isLoadError: false,
  retryLoad: () => {},
  search: '',
  setSearch: () => {},
  removeAssignee: () => {},
  editTranscriptUnlockType: () => {},
  editAllTranscriptUnlockTypes: () => {},
  editBackupRecordingFlag: () => {},
  setGlobalAS1Backup: () => {},
  unlockAITranscripts: () => {},
  onAddUserClick: () => {},
  assignedCount: 0,
  unassignedCount: 0,
  onAssignLicense: () => {},
  subscription: null,
  plan: undefined,
  globalLicensesBackupEnabledValue: 'Mixed',
  openAS1BackupTerms: () => {},
});

type TProps = {
  children: ReactNode;
  orgSlug: string | undefined;
  aOneId: string | undefined;
};

type Assignee = {
  id: string;
  membershipId: string;
  name: string;
  lastname: string;
  email: string;
};

type TAssignLicense = {
  licenseId: string;
  assigneeId: string | null;
};

export const PageProvider = ({ children, aOneId }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const [search, setSearch] = useState('');

  const baId = organization?.billingAccount?.id;
  const subscriptionId = aOneId ?? '';
  const subscriptionKey = subscriptionsKeys.memberDetail(baId ?? '', subscriptionId);

  const subscriptionQuery = useQuery({
    queryKey: subscriptionKey,
    queryFn: createFetchSubscriptionQueryFn(call),
    enabled: !!organization && !!baId && subscriptionId !== '',
  });
  const plansQuery = useQuery({
    queryKey: billingKeys.allAsOnePlans(),
    queryFn: createFetchAsOnePlansQueryFn(call),
  });

  const subscription = subscriptionQuery.data ?? null;
  const plan = plansQuery.data?.find((plan) => plan.id === subscription?.productId);
  const globalLicensesBackupEnabledValue = getGlobalBackupRecordingValue(subscription?.Licenses ?? []);

  const refreshSubscription = () => queryClient.invalidateQueries({ queryKey: subscriptionKey });

  const assignLicenseMutation = useMutation({
    mutationFn: ({ licenseId, assigneeId }: TAssignLicense) =>
      call(
        () =>
          SubscriptionsService.assignLicense({
            baId: baId ?? '',
            requestBody: {
              subscriptionId,
              licenseId,
              assigneeId,
            },
          }),
        { onError: (message) => toast.error(message || 'Unable to update the license.') },
      ),
    onSuccess: (result) => {
      if (result.error) {
        openModal(ModalNamesEnum.GenericErrorModal, {
          message: result.error,
        });
      }
    },
    onSettled: refreshSubscription,
  });

  const setGlobalBackupMutation = useMutation({
    mutationFn: ({ organizationId, value }: { organizationId: string; value: boolean }) =>
      call(
        () =>
          OrganizationsService.setAsOneBackupRecordingAllowedFlag({
            oid: organizationId,
            subscriptionId,
            requestBody: {
              value,
            },
          }),
        { onError: (message) => toast.error(message || 'Unable to update AS1 Backup.') },
      ),
    onSettled: refreshSubscription,
  });

  const acceptBackupTermsMutation = useMutation({
    mutationFn: (organizationId: string) =>
      call(() => OrganizationsService.asOneBackupRecordingAcceptTerms({ oid: organizationId }), {
        onError: (message) => toast.error(message || 'Unable to accept the AS1 Backup terms.'),
      }),
    onSuccess: () => {
      closeModal();
      window.location.reload();
    },
  });

  const isLoading =
    subscriptionQuery.isFetching ||
    plansQuery.isFetching ||
    assignLicenseMutation.isPending ||
    setGlobalBackupMutation.isPending;
  const isLoadError = subscriptionQuery.isError || plansQuery.isError;

  const retryLoad = () => {
    if (subscriptionQuery.isError) {
      void subscriptionQuery.refetch();
    }
    if (plansQuery.isError) {
      void plansQuery.refetch();
    }
  };

  const onAssignLicense = (assignee: Assignee, id: string | null) => {
    if (!baId) {
      return;
    }
    closeModal();
    let licenseId: string;
    if (id) {
      licenseId = id;
    } else {
      const license = subscription?.Licenses.find((license) => license.assigneeId === null);
      licenseId = license?.id ?? '';
    }
    assignLicenseMutation.mutate({ licenseId, assigneeId: assignee.membershipId });
  };

  const unassignedCount = subscription?.Licenses.filter((lic) => lic.assigneeId === null).length || 0;
  const assignedCount = subscription?.Licenses.filter((lic) => lic.assigneeId !== null).length || 0;

  const onAddUserClick = (id: string | null) => {
    openModal(ModalNamesEnum.AddUserLicenseModal, {
      baId,
      subId: aOneId,
      onSuccess: (assignee: Assignee) => onAssignLicense(assignee, id),
    });
  };

  const editTranscriptUnlockType = (licenseId: string, initialValue: string) => {
    openModal(ModalNamesEnum.EditLicenseTranscriptUnlockType, {
      baId,
      subscriptionId: aOneId,
      licenseId,
      initialValue,
      onSuccess: () => void refreshSubscription(),
    });
  };

  const editAllTranscriptUnlockTypes = () => {
    if (!baId) {
      return;
    }
    openModal(ModalNamesEnum.EditSubscriptionTranscriptUnlockType, {
      baId,
      subscriptionId: aOneId,
      initialValue: 'SESSION_TEXT',
      onSuccess: () => void refreshSubscription(),
    });
  };

  const openAS1BackupTerms = () => {
    openModal(ModalNamesEnum.AcceptBackupRecordingTerms, {
      onSuccess: () => acceptBackupTermsMutation.mutate(organization?.id ?? ''),
    });
  };

  const setGlobalAS1Backup = (v: string) => {
    if (!organization?.asOneBackupRecordingTermsAccepted) {
      openAS1BackupTerms();
      return;
    }
    if (!baId) {
      return;
    }
    setGlobalBackupMutation.mutate({ organizationId: organization.id, value: v === '1' });
  };

  const editBackupRecordingFlag = (licenseId: string, initialValue: boolean) => {
    if (!organization?.asOneBackupRecordingTermsAccepted) {
      openAS1BackupTerms();
      return;
    }
    if (!baId) {
      return;
    }
    openModal(ModalNamesEnum.UpdateLicenseBackupRecordingFlag, {
      baId,
      licenseId,
      initialValue,
      onSuccess: () => void refreshSubscription(),
    });
  };

  const removeAssignee = (licenseId: string) => {
    if (!baId) {
      return;
    }
    assignLicenseMutation.mutate({ licenseId, assigneeId: null });
  };

  const unlockAITranscripts = () => {
    if (!baId) {
      return;
    }
    openModal(ModalNamesEnum.UpgradeToAIFeatures);
  };

  return (
    <PageContext.Provider
      value={{
        isLoading,
        isLoadError,
        retryLoad,
        search,
        setSearch,
        removeAssignee,
        editTranscriptUnlockType,
        editAllTranscriptUnlockTypes,
        editBackupRecordingFlag,
        setGlobalAS1Backup,
        unlockAITranscripts,
        onAddUserClick,
        assignedCount,
        unassignedCount,
        onAssignLicense,
        subscription,
        plan,
        globalLicensesBackupEnabledValue,
        openAS1BackupTerms,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
